<template>
  <div class="row">
    <div class="col-lg-12 col-xxl-12">
      <div class="card card-custom card-stretch gutter-b">
        <div class="card-body pb-10 pt-10 page-container">
          <b-form @submit.stop.prevent="onSubmit()">
            <b-row class="mb-5">
              <b-col sm="2">
                <label>
                  <h5>{{ $t("PAGE.DEFAULT.FORM.FIELDS.NAME") }}</h5>
                </label>
              </b-col>
              <b-col sm="10">
                <b-form-input
                  id="name-input"
                  name="name-input"
                  v-model="$v.form.name.$model"
                  :state="validateState('name')"
                  aria-describedby="name-live-feedback"
                ></b-form-input>
                <b-form-invalid-feedback id="name-live-feedback">
                  <span v-if="!$v.form.name.required" class="error">
                    {{ $t("PAGE.DEFAULT.FORM.VALIDATION.REQUIRED") }}
                  </span>
                  <span v-if="!$v.form.name.maxLength" class="error">
                    {{
                      $t("PAGE.DEFAULT.FORM.VALIDATION.MAX_LENGTH", {
                        max: 256
                      })
                    }}
                  </span>
                </b-form-invalid-feedback>
              </b-col>
            </b-row>

            <b-row class="mb-5">
              <b-col sm="2">
                <label>
                  <h5>
                    {{ $t("PAGE.RECEIPT_TYPE.FORM.FIELDS.LIMIT_VALIDATOR") }}
                  </h5>
                </label>
              </b-col>
              <b-col sm="10">
                <b-form-input
                  id="limit-validator-input"
                  name="limit-validator-input"
                  type="number"
                  min="1"
                  v-model.number="$v.form.limit_validator.$model"
                  :state="validateState('limit_validator')"
                  aria-describedby="limit-validator-live-feedback"
                ></b-form-input>
                <b-form-invalid-feedback id="limit-validator-live-feedback">
                  <span v-if="!$v.form.limit_validator.required" class="error">
                    {{ $t("PAGE.DEFAULT.FORM.VALIDATION.REQUIRED") }}
                  </span>
                  <span v-if="!$v.form.limit_validator.integer" class="error">
                    {{ $t("PAGE.DEFAULT.FORM.VALIDATION.INVALID") }}
                  </span>
                  <span v-if="!$v.form.limit_validator.minValue" class="error">
                    {{ $t("PAGE.DEFAULT.FORM.VALIDATION.INVALID") }}
                  </span>
                </b-form-invalid-feedback>
              </b-col>
            </b-row>

            <b-row class="mb-5">
              <b-col sm="2">
                <label>
                  <h5>{{ $t("PAGE.RECEIPT_TYPE.FORM.FIELDS.SORT_NUMBER") }}</h5>
                </label>
              </b-col>
              <b-col sm="10">
                <b-form-input
                  id="sort-number-input"
                  name="sort-number-input"
                  type="number"
                  min="0"
                  v-model.number="$v.form.sort_number.$model"
                  :state="validateState('sort_number')"
                  aria-describedby="sort-number-live-feedback"
                ></b-form-input>
                <b-form-invalid-feedback id="sort-number-live-feedback">
                  <span v-if="!$v.form.sort_number.required" class="error">
                    {{ $t("PAGE.DEFAULT.FORM.VALIDATION.REQUIRED") }}
                  </span>
                  <span v-if="!$v.form.sort_number.integer" class="error">
                    {{ $t("PAGE.DEFAULT.FORM.VALIDATION.INVALID") }}
                  </span>
                  <span v-if="!$v.form.sort_number.minValue" class="error">
                    {{ $t("PAGE.DEFAULT.FORM.VALIDATION.INVALID") }}
                  </span>
                </b-form-invalid-feedback>
              </b-col>
            </b-row>

            <b-row class="mb-5">
              <b-col sm="2">
                <label>
                  <h5>{{ $t("PAGE.RECEIPT_TYPE.FORM.FIELDS.GRAPH_COLOR") }}</h5>
                </label>
              </b-col>
              <b-col sm="10">
                <b-form-input
                  id="graph-color-input"
                  name="graph-color-input"
                  type="color"
                  v-model="form.graph_color"
                ></b-form-input>
              </b-col>
            </b-row>

            <b-row class="mb-5">
              <b-col sm="2">
                <label>
                  <h5>{{ $t("PAGE.RECEIPT_TYPE.FORM.FIELDS.OCR_SYSTEM_PROMPT") }}</h5>
                </label>
              </b-col>
              <b-col sm="10">
                <div class="d-flex align-items-center mb-2">
                  <b-form-file
                    v-model="generateImageFiles"
                    multiple
                    accept="image/*"
                    :placeholder="'เลือกรูปใบเสร็จตัวอย่าง (สูงสุด 20 รูป)'"
                    class="mr-3 flex-grow-1"
                    style="max-width: 400px"
                  ></b-form-file>
                  <b-button
                    variant="outline-primary"
                    :disabled="!generateImageFiles.length || isGenerating"
                    @click.prevent="generatePrompt()"
                  >
                    <span v-if="isGenerating">
                      <b-spinner small></b-spinner> กำลัง Generate...
                    </span>
                    <span v-else>สร้าง Prompt อัตโนมัติ</span>
                  </b-button>
                </div>
                <small class="text-muted d-block mb-2">
                  อัปโหลดรูปใบเสร็จตัวอย่าง 1-20 รูป แล้วกด "สร้าง Prompt อัตโนมัติ" — AI จะวิเคราะห์รูปแบบและสร้าง prompt ให้
                </small>
                <b-form-textarea
                  id="ocr-system-prompt-input"
                  v-model="form.ocr_system_prompt"
                  rows="8"
                  max-rows="20"
                  placeholder="ใส่คำแนะนำเฉพาะสำหรับ AI เพื่อช่วยอ่านใบเสร็จประเภทนี้ให้แม่นยำขึ้น หรือกด 'สร้าง Prompt อัตโนมัติ' ด้านบน"
                ></b-form-textarea>
              </b-col>
            </b-row>

            <b-row class="mb-5" v-if="preview.logo_image">
              <b-col sm="2"></b-col>
              <b-col sm="10">
                <img
                  :src="preview.logo_image"
                  alt="Logo image"
                  class="image-preview"
                />
              </b-col>
            </b-row>
            <b-row class="mb-5">
              <b-col sm="2">
                <label>
                  <h5>{{ $t("PAGE.RECEIPT_TYPE.FORM.FIELDS.LOGO_IMAGE") }}</h5>
                </label>
              </b-col>
              <b-col sm="10">
                <b-form-file
                  v-model="$v.form.logo_image_file.$model"
                  @change="handleImage($event, 'logo_image')"
                  accept="image/*"
                  :placeholder="
                    $t('PAGE.DEFAULT.FORM.PLACEHOLDER.CHOOSE_IMAGE')
                  "
                  drop-placeholder="Drop file here..."
                  :state="validateState('logo_image_file')"
                ></b-form-file>
                <b-form-invalid-feedback>
                  <span v-if="!$v.form.logo_image_file.required" class="error">
                    {{ $t("PAGE.DEFAULT.FORM.VALIDATION.REQUIRED") }}
                  </span>
                </b-form-invalid-feedback>
              </b-col>
            </b-row>

            <b-row class="mb-5" v-if="preview.preview_image">
              <b-col sm="2"></b-col>
              <b-col sm="10">
                <img
                  :src="preview.preview_image"
                  alt="Preview image"
                  class="image-preview"
                />
              </b-col>
            </b-row>
            <b-row class="mb-5">
              <b-col sm="2">
                <label>
                  <h5>
                    {{ $t("PAGE.RECEIPT_TYPE.FORM.FIELDS.PREVIEW_IMAGE") }}
                  </h5>
                </label>
              </b-col>
              <b-col sm="10">
                <b-form-file
                  v-model="$v.form.preview_image_file.$model"
                  @change="handleImage($event, 'preview_image')"
                  accept="image/*"
                  :placeholder="
                    $t('PAGE.DEFAULT.FORM.PLACEHOLDER.CHOOSE_IMAGE')
                  "
                  drop-placeholder="Drop file here..."
                  :state="validateState('preview_image_file')"
                ></b-form-file>
                <b-form-invalid-feedback>
                  <span
                    v-if="!$v.form.preview_image_file.required"
                    class="error"
                  >
                    {{ $t("PAGE.DEFAULT.FORM.VALIDATION.REQUIRED") }}
                  </span>
                </b-form-invalid-feedback>
              </b-col>
            </b-row>

            <b-row>
              <b-col sm="12">
                <b-button class="float-right" @click.prevent="cancel()">{{
                  $t("PAGE.DEFAULT.FORM.BTN_CANCEL")
                }}</b-button>
                <b-button
                  type="submit"
                  variant="primary"
                  class="float-right mr-3"
                >
                  {{ $t("PAGE.DEFAULT.FORM.BTN_SUBMIT") }}
                </b-button>
              </b-col>
            </b-row>
          </b-form>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.image-preview {
  width: 120px;
  height: 120px;
  object-fit: cover;
  object-position: center;
  border: solid 1px #ccc;
  border-radius: 12px;
}
</style>

<script>
import { validationMixin } from "vuelidate";
import {
  required,
  maxLength,
  requiredIf,
  integer,
  minValue
} from "vuelidate/lib/validators";
import Swal from "sweetalert2";
import { SET_BREADCRUMB } from "@/core/services/store/breadcrumbs.module";
import {
  FIND_RECEIPT_TYPE,
  CREATE_RECEIPT_TYPE,
  UPDATE_RECEIPT_TYPE,
  GENERATE_OCR_PROMPT
} from "@/core/services/store/receipt_type.module";

export default {
  name: "ReceiptTypeForm",
  mixins: [validationMixin],
  data() {
    return {
      id: 0,
      form: {
        name: "",
        logo_image: null,
        logo_image_file: [],
        preview_image: null,
        preview_image_file: [],
        limit_validator: 1,
        sort_number: 0,
        graph_color: "#00B14F",
        ocr_system_prompt: null,
        status_id: 1
      },
      preview: {
        logo_image: null,
        preview_image: null
      },
      existingImagePath: {
        logo_image: null,
        preview_image: null
      },
      generateImageFiles: [],
      isGenerating: false
    };
  },
  validations: {
    form: {
      name: {
        required,
        maxLength: maxLength(256)
      },
      limit_validator: {
        required,
        integer,
        minValue: minValue(1)
      },
      sort_number: {
        required,
        integer,
        minValue: minValue(0)
      },
      logo_image_file: {
        required: requiredIf(function () {
          return !this.id && !this.form.logo_image;
        })
      },
      preview_image_file: {
        required: requiredIf(function () {
          return !this.id && !this.form.preview_image;
        })
      }
    }
  },
  mounted() {
    const rawId = this.$route.params.id;
    this.id = rawId ? Number(rawId) : 0;
    if (Number.isNaN(this.id)) {
      this.id = 0;
    }

    this.$store.dispatch(SET_BREADCRUMB, [
      {
        title: `${this.$t("MENU.RECEIPT_TYPE_MANAGEMENT.TITLE")}`,
        route: this.id ? "../../receipt-type" : "../receipt-type"
      },
      { title: `${this.$t("PAGE.DEFAULT.FORM.TITLE")}` }
    ]);

    if (this.id) {
      this.getData(this.id);
    }
  },
  methods: {
    validateState(name) {
      const { $dirty, $error } = this.$v.form[name];
      return $dirty ? !$error : null;
    },
    handleImage(event, field) {
      const selectedImage = event.target.files[0];

      if (!selectedImage) {
        return;
      }

      this.encodeBase64(selectedImage, field);
    },
    encodeBase64(file, field) {
      const reader = new FileReader();

      reader.onload = (e) => {
        const base64 = e.target.result;
        this.form[field] = base64;
        this.preview[field] = base64;
      };
      reader.readAsDataURL(file);
    },
    onSubmit() {
      this.$v.form.$touch();
      if (this.$v.form.$anyError) {
        return;
      }

      this.submitData();
    },
    async ensureExistingImageBase64(field) {
      if (this.form[field]) {
        return true;
      }

      const path = this.existingImagePath[field];
      if (!path) {
        return false;
      }

      try {
        const response = await fetch(`${process.env.VUE_APP_IMAGE}${path}`);
        const blob = await response.blob();
        const base64 = await this.blobToBase64(blob);
        this.form[field] = base64;
        return true;
      } catch (error) {
        return false;
      }
    },
    blobToBase64(blob) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    },
    async submitData() {
      if (this.id) {
        const okLogo = await this.ensureExistingImageBase64("logo_image");
        const okPreview = await this.ensureExistingImageBase64("preview_image");

        if (!okLogo || !okPreview) {
          Swal.fire({
            icon: "warning",
            text: `${this.$t("PAGE.DEFAULT.FORM.VALIDATION.REQUIRED")}`
          });
          return;
        }
      }

      Swal.fire({
        title: `${this.$t("LOADING")}`,
        allowEscapeKey: false,
        allowOutsideClick: false,
        timer: 36000,
        onOpen: () => {
          Swal.showLoading();

          const action = this.id ? UPDATE_RECEIPT_TYPE : CREATE_RECEIPT_TYPE;
          const payload = this.id
            ? { id: this.id, data: this.form }
            : { data: this.form };

          this.$store
            .dispatch(action, payload)
            .then(({ data }) => {
              Swal.close();
              if (data.status === "success" && data.data) {
                Swal.fire({
                  icon: "success",
                  text: `${this.$t("RESPONSE_MESSAGE.SAVE_DATA_SUCCESS")}`
                });
                this.$router.push(
                  this.id ? "../../receipt-type" : "../receipt-type"
                );
              } else if (data.status === "error" && data.error) {
                Swal.fire({
                  icon: "error",
                  text: `${this.$t("RESPONSE_MESSAGE." + data.error)}`,
                  confirmButtonText: `${this.$t("PAGE.DEFAULT.FORM.BTN_OK")}`
                });
              }
            })
            .catch((error) => {
              Swal.close();
              Swal.fire({
                icon: "error",
                text: `${error}`,
                confirmButtonText: `${this.$t("PAGE.DEFAULT.FORM.BTN_OK")}`
              });
            });
        }
      });
    },
    getData(id) {
      Swal.fire({
        title: `${this.$t("LOADING")}`,
        allowEscapeKey: false,
        allowOutsideClick: false,
        timer: 36000,
        onOpen: () => {
          Swal.showLoading();
          this.$store
            .dispatch(FIND_RECEIPT_TYPE, { id })
            .then(({ data }) => {
              Swal.close();
              if (data.status === "success" && data.data) {
                this.form.name = data.data.name || "";
                this.form.limit_validator = Number(
                  data.data.limit_validator || 1
                );
                this.form.sort_number = Number(data.data.sort_number || 0);
                this.form.graph_color = data.data.graph_color || "#00B14F";
                this.form.ocr_system_prompt = data.data.ocr_system_prompt || null;
                this.form.status_id = data.data.status_id || 1;

                this.existingImagePath.logo_image =
                  data.data.logo_image || null;
                this.existingImagePath.preview_image =
                  data.data.preview_image || null;
                this.preview.logo_image = data.data.logo_image
                  ? `${process.env.VUE_APP_IMAGE}${data.data.logo_image}`
                  : null;
                this.preview.preview_image = data.data.preview_image
                  ? `${process.env.VUE_APP_IMAGE}${data.data.preview_image}`
                  : null;
              } else if (data.status === "error" && data.error) {
                Swal.fire({
                  icon: "error",
                  text: `${this.$t("RESPONSE_MESSAGE." + data.error)}`,
                  confirmButtonText: `${this.$t("PAGE.DEFAULT.FORM.BTN_OK")}`
                });
              }
            })
            .catch((error) => {
              Swal.close();
              Swal.fire({
                icon: "error",
                text: `${error}`,
                confirmButtonText: `${this.$t("PAGE.DEFAULT.FORM.BTN_OK")}`
              });
            });
        }
      });
    },
    cancel() {
      this.$router.push(this.id ? "../../receipt-type" : "../receipt-type");
    },
    fileToBase64(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    },
    // ย่อรูปด้วย canvas ก่อนแปลง base64 เพื่อลดขนาด payload (เร็วขึ้น + ประหยัด token)
    // ด้านยาวสุดไม่เกิน maxEdge px, export เป็น JPEG quality 0.85 — ตัวอักษรในใบเสร็จยังอ่านออก
    async downscaleImage(file, maxEdge = 1600, quality = 0.85) {
      const dataUrl = await this.fileToBase64(file);
      return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          let { width, height } = img;
          const longest = Math.max(width, height);
          if (longest > maxEdge) {
            const scale = maxEdge / longest;
            width = Math.round(width * scale);
            height = Math.round(height * scale);
          }
          try {
            const canvas = document.createElement("canvas");
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext("2d");
            ctx.drawImage(img, 0, 0, width, height);
            resolve(canvas.toDataURL("image/jpeg", quality));
          } catch (e) {
            resolve(dataUrl); // ถ้าย่อไม่สำเร็จ ใช้รูปเดิม
          }
        };
        img.onerror = () => resolve(dataUrl);
        img.src = dataUrl;
      });
    },
    async generatePrompt() {
      if (!this.generateImageFiles.length || this.isGenerating) return;
      this.isGenerating = true;
      try {
        const files = Array.from(this.generateImageFiles).slice(0, 20);
        const images = await Promise.all(files.map((f) => this.downscaleImage(f)));

        const { data } = await this.$store.dispatch(GENERATE_OCR_PROMPT, {
          data: { images }
        });

        if (data.status === "success" && data.data && data.data.prompt) {
          this.form.ocr_system_prompt = data.data.prompt;
          this.generateImageFiles = [];
          Swal.fire({
            icon: "success",
            text: "สร้าง Prompt สำเร็จ กรุณาตรวจสอบและแก้ไขก่อนบันทึก",
            timer: 3000,
            showConfirmButton: false
          });
        } else {
          Swal.fire({
            icon: "error",
            text: (data && data.error) || "ไม่สามารถสร้าง Prompt ได้ กรุณาลองใหม่",
            confirmButtonText: "ตกลง"
          });
        }
      } catch (error) {
        Swal.fire({
          icon: "error",
          text: `${error}`,
          confirmButtonText: "ตกลง"
        });
      } finally {
        this.isGenerating = false;
      }
    }
  }
};
</script>
